import { toCutsceneJournalEntryId } from '@sorrem/shared';
import { TabStrip, type TabDef } from '@sorrem-client/ui/components';
import { useAppNavigate, useCompendium, useJournal } from '@sorrem-client/ui/hooks';
import { CutscenePlayer } from '@sorrem-client/ui/features/Cutscene';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { findCompendiumVision, resolveCompendiumSelection, type JourneySection, type CompendiumSelection } from './compendium-pages';
import { CompendiumBook } from './components/CompendiumBook';
import { JournalBook } from './components/JournalBook';
import { VisionPlayer } from './components/VisionPlayer';
import styles from './JourneyMainPanel.module.scss';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

const journeySections: TabDef<JourneySection>[] = [
    { id: 'journal', label: 'Journal' },
    { id: 'compendium', label: 'Compendium' },
];

interface JourneyMainPanelProps {
    section: JourneySection;
    onSectionChange: (section: JourneySection) => void;
    selectedEntryId: string | null;
    onSelectEntry: (entryId: string) => void;
    compendiumSelection: CompendiumSelection | null;
    onSelectCompendium: (selection: CompendiumSelection) => void;
}

/**
 * The Journey: the character's own story (the Journal) and the visions
 * they have received (the Compendium), both kept in one field journal, the
 * Compendium written in the back of it. It lies open on the writing table: no panel frame, and the whole
 * screen below the header is theirs. What can be done with an entry or a
 * vision is on its own page.
 */
export function JourneyMainPanel({
    section,
    onSectionChange,
    selectedEntryId,
    onSelectEntry,
    compendiumSelection,
    onSelectCompendium,
}: JourneyMainPanelProps): JSX.Element {
    const { entries, isLoaded } = useJournal();
    const { compendium, isLoaded: isCompendiumLoaded } = useCompendium();
    const navigate = useAppNavigate();
    // A replay is its own route, /character/journey/cutscenes/:cutsceneId; so is a relive, /character/journey/visions/:visionId.
    const { cutsceneId: replayingId, visionId: relivingId } = useParams<{ cutsceneId?: string; visionId?: string }>();
    const isReplayWatched = entries.some((entry) => entry.kind === 'cutscene' && entry.cutscene.cutsceneId === replayingId);
    const relivingVision = relivingId !== undefined ? findCompendiumVision(compendium, relivingId) : undefined;

    useEffect(() => {
        if (replayingId === undefined) {
            return;
        }
        // Keep the replayed cutscene selected, so the book is where it was afterward.
        onSectionChange('journal');
        onSelectEntry(toCutsceneJournalEntryId(replayingId));
        // Only cutscenes the character has watched can be replayed; a link to
        // any other would show story they haven't reached.
        if (isLoaded && !isReplayWatched) {
            navigate('/character/journey', { replace: true });
        }
    }, [isLoaded, isReplayWatched, navigate, onSectionChange, onSelectEntry, replayingId]);

    useEffect(() => {
        if (relivingId === undefined) {
            return;
        }
        onSectionChange('compendium');
        onSelectCompendium({ kind: 'vision', visionId: relivingId });
        // Only a vision the character has received can be relived.
        if (isCompendiumLoaded && relivingVision === undefined) {
            navigate('/character/journey', { replace: true });
        }
    }, [isCompendiumLoaded, navigate, onSectionChange, onSelectCompendium, relivingId, relivingVision]);

    const openVision = (visionId: string): void => {
        onSectionChange('compendium');
        onSelectCompendium({ kind: 'vision', visionId });
    };

    return (
        <div className={styles.journeyPanel}>
            <TabStrip className={styles.sectionSwitch} tabs={journeySections} activeTab={section} onTabChange={onSectionChange} {...toTourTargetProps('journey-books')} />

            {section === 'journal' ? (
                <JournalBook entries={entries} selectedEntryId={selectedEntryId} onSelectEntry={onSelectEntry} onOpenVision={openVision} />
            ) : (
                <CompendiumBook
                    compendium={compendium}
                    selection={resolveCompendiumSelection(compendium, compendiumSelection)}
                    onSelect={onSelectCompendium}
                />
            )}

            {/* Replaying only watches it again; it's already recorded as seen. */}
            {replayingId !== undefined && isReplayWatched && (
                <CutscenePlayer cutsceneId={replayingId} onFinished={() => navigate('/character/journey')} />
            )}
            {relivingVision !== undefined && (
                <VisionPlayer vision={relivingVision} onFinished={() => navigate('/character/journey')} />
            )}
        </div>
    );
}
